import type { Map as LeafletMap } from 'leaflet';
import { useEffect, useRef } from 'preact/hooks';

/**
 * 416 (#1431, mock-up screens 6 and 10): the pickup location's driving area, for the customer's own reference: the
 * outline and the pickup point, before paying (`pickup_zone` on `GET /cars/:id`) and on a paid booking
 * (`pickup_zone` on `GET /bookings/:id`). Never a route. Leaflet is loaded only when the card is shown, so the app's
 * first load is unchanged (405's rule).
 */
export interface PickupZone {
  location_name: string;
  polygon: [number, number][];
  pickup?: [number, number] | null;
}

const LINE = {
  booking: 'driving outside the outline may be charged per km',
  rental: 'drive inside the outline to avoid per-km charges',
} as const;

export function DrivingAreaCard({ zone, when }: { zone: PickupZone; when: 'booking' | 'rental' }) {
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<LeafletMap | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const L = (await import('leaflet')).default;
      await import('leaflet/dist/leaflet.css');
      if (cancelled || !container.current || map.current) return;
      const m = L.map(container.current, { attributionControl: true }).setView(
        [3.139, 101.6869],
        13,
      );
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19,
      }).addTo(m);
      const outline = L.polygon(
        zone.polygon.map(([lng, lat]) => [lat, lng] as [number, number]),
        { color: '#ffd800', weight: 2, dashArray: '6 4', fillColor: '#ffd800', fillOpacity: 0.12 },
      ).addTo(m);
      if (zone.pickup) {
        L.circleMarker([zone.pickup[1], zone.pickup[0]], {
          radius: 6,
          color: '#ffffff',
          weight: 2,
          fillColor: '#0070c0',
          fillOpacity: 1,
        }).addTo(m);
      }
      m.fitBounds(outline.getBounds(), { padding: [12, 12] });
      map.current = m;
    })();
    return () => {
      cancelled = true;
    };
  }, [zone]);
  useEffect(
    () => () => {
      map.current?.remove();
      map.current = null;
    },
    [],
  );

  return (
    <div className="mt-3 bg-surface-card rounded-xl p-4" data-testid="driving-area">
      <p data-testid="driving-area-title" className="font-semibold text-text">
        {when === 'booking' ? 'Driving area' : 'Your driving area'}
      </p>
      <p data-testid="driving-area-line" className="mb-2 text-xs text-text-muted">
        {zone.location_name} · {LINE[when]}
      </p>
      {/* `isolate`: Leaflet's panes sit at z-index 400+, above the fixed bottom nav (z-50) and the dialogs. */}
      <div
        ref={container}
        className="isolate h-40 w-full rounded-lg overflow-hidden bg-surface-mid"
        aria-label={`Map of the ${zone.location_name} driving area`}
      />
    </div>
  );
}
